import React, { useState, useEffect, useRef, useMemo } from "react";
import QRCode from "react-qr-code";
import {
  Loader2,
  MapPin,
  Sunrise as SunriseIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme as useAppTheme } from "../theme/ThemeContext";
import { checkAdmin, getTvData } from "../services/api";
import { auth } from "../config/firebase";
import {
  acquireTvSse,
  releaseTvSse,
  addTvCommandListener,
  addTvStatusListener,
  updateTvLastView,
  updateTvSettings,
  updateTvOverlayState,
} from "../services/tvSseManager";
import CountdownOverlay from "../components/TeleView/CountdownOverlay";
import { useCountdownOverlay } from "../hooks/useCountdownOverlay";
import TVSettingsDialog from "../components/TeleView/TVSettingsDialog";
import TVControls from "../components/TeleView/TVControls";
import { useTVSettings, VIEW_MODES, THEME_MODES } from "../hooks/useTVSettings";
import WakeLockVideo from "../components/WakeLockVideo";

// SSE API URL for TV remote control
const MASJID_API = import.meta.env.VITE_MASJID_API;

// Set to true to show Adhan/Iqama test buttons in the controls
const TEST_MODE = false;
const REMOTE_SETTINGS_COMMANDS = new Set([
  "changeTheme",
  "resetSettings",
  "setThemeMode",
  "setPrimaryColor",
  "setCustomColor",
  "setShowTomorrow",
  "setZoomLevel",
  "setAdhanEnabled",
  "setIqamaEnabled",
  "setAdhanCountdownMinutes",
  "setIqamaCountdownMinutes",
  "setAdhanScreenDurationMinutes",
  "setIqamaScreenDurationMinutes",
  "setJummahSilenceEnabled",
  "setJummahSilenceDurationMinutes",
  "setForbiddenScreenEnabled",
  "setForbiddenScreenDurationMinutes",
  "updateSettings",
]);
const REMOTE_SETTINGS_SUPPRESS_MS = 1200;
const REMOTE_VIEW_SUPPRESS_MS = 1200;

// Error boundary wrapper for QR code rendering
const QRCodeWrapper = ({ value, size = 180 }) => {
  try {
    return <QRCode value={value || "https://example.com"} size={size} level="H" />;
  } catch (error) {
    console.error("QR Code error:", error);
    return (
      <div
        className="flex items-center justify-center border-2 border-dashed border-gray-400"
        style={{ width: size, height: size }}
      >
        <p className="text-sm text-muted-foreground">QR Code Error</p>
      </div>
    );
  }
};

export default function PrayerTimesView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { appearance, theme } = useAppTheme();

  const preloaded = location.state?.preload || null;
  const preloadedMasjidId = location.state?.masjidId || null;
  const hasPreloadedData = Boolean(preloaded?.masjidData || preloaded?.todayPrayerTimes);

  const [masjidData, setMasjidData] = useState(preloaded?.masjidData || null);
  const [todayPrayerTimes, setTodayPrayerTimes] = useState(
    preloaded?.todayPrayerTimes || null
  );
  const [tomorrowPrayerTimes, setTomorrowPrayerTimes] = useState(
    preloaded?.tomorrowPrayerTimes || null
  );
  const [loading, setLoading] = useState(!hasPreloadedData);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  // Initialize fullscreen state from actual document state
  const [isFullscreen, setIsFullscreen] = useState(() => !!document.fullscreenElement);
  const [controlsVisible, setControlsVisible] = useState(true);
  const inactivityTimeoutRef = useRef(null);
  const wasFullscreenRef = useRef(false); // Track if we were in fullscreen before ESC (for Firefox workaround)
  const [masjidId, setMasjidId] = useState(preloadedMasjidId);
  const lastLoadedDateRef = useRef(new Date().toDateString());
  const remoteSettingsSyncRef = useRef(0);
  const remoteViewSyncRef = useRef(0);

  // SSE Remote Control state
  const [sseConnected, setSseConnected] = useState(false);
  const [viewSwitching, setViewSwitching] = useState(false);

  // Settings dialog state
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Super admin state for navigation
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // TV Settings hook for shared settings management
  const tvSettings = useTVSettings({ globalTheme: theme, globalAppearance: appearance });

  // Set viewMode to TODAY when this page loads (for settings dialog to show correct view)
  useEffect(() => {
    tvSettings.setViewMode(VIEW_MODES.TODAY);
  }, []);

  // Theme mode selection: light | dark | auto (view-local, doesn't affect global)
  const [themeMode, setThemeMode] = useState(
    () => localStorage.getItem("ptv-theme-mode") || "auto"
  );
  // Local appearance state for this view only
  const [localAppearance, setLocalAppearance] = useState(() => {
    const mode = localStorage.getItem("ptv-theme-mode") || "auto";
    if (mode === "light") return "light";
    if (mode === "dark") return "dark";
    // For auto mode, start with light and let auto-theme update it
    return "light";
  });

  // Sync tvSettings with local state
  useEffect(() => {
    setThemeMode(tvSettings.themeMode);
  }, [tvSettings.themeMode]);

  useEffect(() => {
    setLocalAppearance(tvSettings.localAppearance);
  }, [tvSettings.localAppearance]);

  // Test mode state (for test buttons - currently commented out)
  const [testMode, setTestMode] = useState(null); // null | "adhan" | "iqama" | "jummah" | "sunrise"
  const testTimerRef = useRef(null);

  // Use the modular countdown overlay hook
  const countdownOverlay = useCountdownOverlay({
    todayPrayerTimes,
    tomorrowPrayerTimes,
    testMode: Boolean(testMode),
    tvSettings,
    paused: settingsOpen,
  });

  // Destructure commonly used values for cleaner code
  const {
    nextPrayer,
    countdown,
    activePrayer,
    countdownPhase,
    countdownLabel,
    overlayMode,
    overlayPrayerName,
    overlayCountdownSeconds,
    overlayType,
    screenDimmed,
    iqamaExtraDimmed,
    isTomorrowFajr,
    tomorrowFajrTimeStr,
    formatTimeDisplay,
    // Setters for test mode
    setOverlayMode,
    setOverlayPrayerName,
    setOverlayType,
    setOverlayCountdownSeconds,
    setScreenDimmed,
    setIqamaExtraDimmed,
    clearOverlay,
  } = countdownOverlay;

  // ESC key handler to dismiss overlays (prevents exiting fullscreen when overlay is active)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && countdownOverlay.overlayMode) {
        // Prevent default ESC behavior (exiting fullscreen) when overlay is showing
        e.preventDefault();
        e.stopPropagation();
        // Clear test mode if active
        if (testMode) {
          if (testTimerRef.current) clearInterval(testTimerRef.current);
          setTestMode(null);
        }
        clearOverlay(true); // true = manual dismiss, prevents re-triggering for 5 minutes
      }
    };

    // Use capture phase to intercept ESC before browser handles fullscreen exit
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [countdownOverlay.overlayMode, testMode, clearOverlay]);

  // Report overlay state changes to SSE so app remote can show/hide dismiss button
  useEffect(() => {
    const overlayActive = Boolean(countdownOverlay.overlayMode);
    updateTvOverlayState(overlayActive);
  }, [countdownOverlay.overlayMode]);

  // Update date every second
  useEffect(() => {
    const interval = setInterval(() => setCurrentDate(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Reload data at midnight
  useEffect(() => {
    const nowStr = currentDate.toDateString();
    if (nowStr !== lastLoadedDateRef.current && masjidId) {
      console.log("Date changed, reloading prayer times...");
      lastLoadedDateRef.current = nowStr;
      loadPrayerTimes(masjidId);
    }
  }, [currentDate, masjidId]);

  // Fullscreen detection with Firefox workaround
  // Firefox exits fullscreen on ESC before JS can prevent it, so we re-enter if overlay was showing
  useEffect(() => {
    const handleFullscreenChange = async () => {
      const nowFullscreen = !!document.fullscreenElement;
      const wasFullscreen = wasFullscreenRef.current;
      
      // If we just exited fullscreen while an overlay was showing, re-enter fullscreen
      // This handles Firefox which doesn't allow preventing ESC from exiting fullscreen
      if (wasFullscreen && !nowFullscreen && countdownOverlay.overlayMode) {
        // Clear the overlay first (user wanted to dismiss it with ESC)
        if (testMode) {
          if (testTimerRef.current) clearInterval(testTimerRef.current);
          setTestMode(null);
        }
        clearOverlay();
        
        // Re-enter fullscreen after a brief delay
        setTimeout(async () => {
          try {
            const element = document.documentElement;
            if (element.requestFullscreen) await element.requestFullscreen();
          } catch (err) {
            // User may have navigated away or browser blocked it
            console.log("Could not re-enter fullscreen:", err);
          }
        }, 50);
      }
      
      wasFullscreenRef.current = nowFullscreen;
      setIsFullscreen(nowFullscreen);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [countdownOverlay.overlayMode, testMode, clearOverlay]);

  // Auto-hide controls (double timeout) and close open menus when hiding
  useEffect(() => {
    const resetTimer = () => {
      if (inactivityTimeoutRef.current)
        clearTimeout(inactivityTimeoutRef.current);
      setControlsVisible(true);
      inactivityTimeoutRef.current = setTimeout(() => {
        setControlsVisible(false);
      }, 6000);
    };

    const onActivity = () => resetTimer();
    window.addEventListener("mousemove", onActivity);
    window.addEventListener("click", onActivity);
    window.addEventListener("touchstart", onActivity);
    window.addEventListener("keydown", onActivity);

    resetTimer();
    return () => {
      window.removeEventListener("mousemove", onActivity);
      window.removeEventListener("click", onActivity);
      window.removeEventListener("touchstart", onActivity);
      window.removeEventListener("keydown", onActivity);
      if (inactivityTimeoutRef.current)
        clearTimeout(inactivityTimeoutRef.current);
    };
  }, []);

  const enterFullscreen = async () => {
    try {
      const element = document.documentElement;
      if (element.requestFullscreen) await element.requestFullscreen();
    } catch (err) {
      console.error("Error entering fullscreen:", err);
    }
  };

  const exitFullscreen = async () => {
    try {
      if (document.exitFullscreen) await document.exitFullscreen();
    } catch (err) {
      console.error("Error exiting fullscreen:", err);
    }
  };

  const handleFullscreenToggle = () =>
    isFullscreen ? exitFullscreen() : enterFullscreen();

  // Note: Automatic fullscreen restoration removed.
  // Browsers require user interaction to enter fullscreen mode.
  // Users can manually enter fullscreen using the fullscreen button or F11.

  // Only exit fullscreen if explicitly navigating away via back button
  useEffect(() => {
    return () => {
      const shouldExitFullscreen = sessionStorage.getItem('exit-fullscreen') === 'true';
      sessionStorage.removeItem('exit-fullscreen');
      
      if (document.fullscreenElement && shouldExitFullscreen) {
        exitFullscreen();
      } else if (document.fullscreenElement) {
        // Store fullscreen state for next view to restore
        sessionStorage.setItem('tv-fullscreen-state', 'true');
      }
    };
  }, []);

  // Auto theme switching based on sunrise/maghrib when mode is 'auto'
  const lastDesiredRef = useRef(null);
  useEffect(() => {
    // Handle non-auto modes
    if (tvSettings.themeMode !== "auto") {
      lastDesiredRef.current = null;
      return;
    }
    
    // For AUTO mode, compute appearance based on sunrise/maghrib
    if (!todayPrayerTimes) {
      // No prayer times yet, use a sensible default based on current hour
      const hour = currentDate.getHours();
      const defaultAppearance = (hour >= 18 || hour < 6) ? "dark" : "light";
      if (lastDesiredRef.current !== defaultAppearance) {
        lastDesiredRef.current = defaultAppearance;
        setLocalAppearance(defaultAppearance);
        tvSettings.setLocalAppearance(defaultAppearance);
      }
      return;
    }
    
    const sunriseStr = todayPrayerTimes?.sunrise;
    const maghribData = todayPrayerTimes?.maghrib;
    const maghribStr = maghribData?.adhan || maghribData;

    const parseWithInference = (str, key) => {
      if (!str) return null;
      const d = new Date();
      const m = str.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
      if (!m) return null;
      let h = parseInt(m[1], 10);
      const min = parseInt(m[2], 10);
      const ap = m[3]?.toUpperCase();
      const k = (key || "").toLowerCase();
      if (!ap) {
        if (k === "sunrise") {
          if (h === 12) h = 0;
        } else {
          if (h >= 1 && h <= 11) h += 12;
        }
      } else {
        if (ap === "PM" && h !== 12) h += 12;
        if (ap === "AM" && h === 12) h = 0;
      }
      d.setHours(h, min, 0, 0);
      return d;
    };

    const sunrise = parseWithInference(sunriseStr, "sunrise");
    const maghrib = parseWithInference(maghribStr, "maghrib");
    const now = new Date();
    if (sunrise && maghrib) {
      const desiredAppearance = now >= maghrib || now < sunrise ? "dark" : "light";
      if (lastDesiredRef.current !== desiredAppearance) {
        lastDesiredRef.current = desiredAppearance;
        setLocalAppearance(desiredAppearance);
        // Also update tvSettings so the dialog knows the current appearance
        tvSettings.setLocalAppearance(desiredAppearance);
      }
    }
  }, [tvSettings.themeMode, todayPrayerTimes, currentDate, tvSettings]);

  // Update local appearance when user manually selects light/dark
  useEffect(() => {
    localStorage.setItem("ptv-theme-mode", themeMode);
    if (themeMode === "light") {
      setLocalAppearance("light");
    } else if (themeMode === "dark") {
      setLocalAppearance("dark");
    }
  }, [themeMode]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      if (authLoading) return;
      if (!user?.email) {
        setError("Please log in to view this page");
        setLoading(false);
        return;
      }

      try {
        if (!hasPreloadedData) {
          setLoading(true);
        }
        setError(null);
        const adminData = await checkAdmin(user.email);
        if (!adminData.isAdmin) {
          setError("You do not have admin privileges");
          setLoading(false);
          return;
        }

        // Set super admin status for navigation
        setIsSuperAdmin(adminData.role === "super");

        const masjidIdValue =
          adminData.role === "super"
            ? adminData.activeMasjidId
            : adminData.masjidId;
        if (!masjidIdValue) {
          setError("No masjid assigned.");
          setLoading(false);
          return;
        }

        setMasjidId(masjidIdValue);

        // Load TV data from API (masjid + prayer times)
        const tvData = await getTvData(masjidIdValue);
        if (tvData.ok) {
          setMasjidData(tvData.masjid);
          setTodayPrayerTimes(tvData.todayPrayerTimes);
          setTomorrowPrayerTimes(tvData.tomorrowPrayerTimes);
        }
      } catch (err) {
        console.error("Error loading data:", err);
        setError("Failed to load data.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user?.email, authLoading, hasPreloadedData]);

  // SSE Remote Control - Connect to backend SSE endpoint for real-time view switching

  // Generate a friendly device name from user agent
  const getDeviceName = () => {
    const ua = navigator.userAgent;
    let browser = "Browser";
    let os = "Device";

    // Detect browser
    if (ua.includes("Chrome") && !ua.includes("Edg")) browser = "Chrome";
    else if (ua.includes("Safari") && !ua.includes("Chrome")) browser = "Safari";
    else if (ua.includes("Firefox")) browser = "Firefox";
    else if (ua.includes("Edg")) browser = "Edge";

    // Detect OS
    if (ua.includes("Mac")) os = "Mac";
    else if (ua.includes("Windows")) os = "Windows";
    else if (ua.includes("Linux")) os = "Linux";
    else if (ua.includes("Android")) os = "Android";
    else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";

    return `${browser} on ${os}`;
  };

  // Get or create a session ID for this browser tab (persists across view changes)
  const getSessionId = () => {
    let sessionId = sessionStorage.getItem("tv-session-id");
    if (!sessionId) {
      sessionId = Math.random().toString(36).substring(2, 10);
      sessionStorage.setItem("tv-session-id", sessionId);
    }
    return sessionId;
  };

  // Live remote control (Pro masjids only). Non-Pro skip the listener entirely;
  // the TV display still works, just without remote control.
  useEffect(() => {
    if (!masjidId || !user) return;
    if (masjidData?.subscription !== "pro") return;

    const deviceName = getDeviceName();
    const sessionId = getSessionId();
    // Accept forceRefresh param for reconnection scenarios where token may have expired
    const getToken = (forceRefresh = false) => auth.currentUser?.getIdToken(forceRefresh);

    acquireTvSse({
      masjidId,
      masjidApi: MASJID_API,
      sessionId,
      deviceName,
      initialView: "prayer-times",
      getToken,
      // Send current TV settings for app remote to sync
      themeMode: tvSettings.themeMode,
      primaryColor: tvSettings.primaryColor,
      showTomorrow: tvSettings.showTomorrow,
      zoomLevel: tvSettings.zoomLevel,
      adhanEnabled: tvSettings.adhanEnabled,
      iqamaEnabled: tvSettings.iqamaEnabled,
      adhanCountdownMinutes: tvSettings.adhanCountdownMinutes,
      iqamaCountdownMinutes: tvSettings.iqamaCountdownMinutes,
      adhanScreenDurationMinutes: tvSettings.adhanScreenDurationMinutes,
      iqamaScreenDurationMinutes: tvSettings.iqamaScreenDurationMinutes,
      jummahSilenceEnabled: tvSettings.jummahSilenceEnabled,
      jummahSilenceDurationMinutes: tvSettings.jummahSilenceDurationMinutes,
      forbiddenScreenEnabled: tvSettings.forbiddenScreenEnabled,
      forbiddenScreenDurationMinutes: tvSettings.forbiddenScreenDurationMinutes,
      tomorrowChangesBanner: tvSettings.tomorrowChangesBanner,
    });

    const unsubscribeStatus = addTvStatusListener((status) => {
      setSseConnected(Boolean(status.connected));
      if (status.clientId) {
        updateTvLastView("prayer-times");
      }
    });

    const unsubscribeCommand = addTvCommandListener((data) => {
      if (REMOTE_SETTINGS_COMMANDS.has(data.command)) {
        remoteSettingsSyncRef.current = Date.now();
      }
      if (data.command === "changeView") {
        remoteViewSyncRef.current = Date.now();
        const targetView = data.view;

        // If we need to navigate to TeleView, switch views without dropping the SSE
        if (targetView !== "prayer-times" && targetView !== "today") {
          // Show loading state
          setViewSwitching(true);

          // Small delay for UI feedback
          setTimeout(() => {
            // Navigate to TeleView with appropriate view mode
            if (targetView === "teleview" || targetView === "multiday") {
              navigate("/admin/tv-viewer", { state: { masjidId, viewMode: "none" } });
            } else if (targetView === "events") {
              navigate("/admin/tv-viewer", { state: { masjidId, viewMode: "events" } });
            } else if (targetView === "images") {
              navigate("/admin/tv-viewer", { state: { masjidId, viewMode: "images" } });
            }
          }, 100);
        }
        // If prayer-times or today, stay on this view (do nothing)
      }

      // Handle theme change command (legacy - simple dark/light toggle)
      if (data.command === "changeTheme" && data.theme) {
        if (data.theme === "light") {
          tvSettings.setThemeMode("light");
        } else if (data.theme === "dark") {
          tvSettings.setThemeMode("dark");
        } else if (data.theme === "auto") {
          tvSettings.setThemeMode("auto");
        }
      }

      // Handle changeTheme with extended settings (from mobile remote)
      if (data.command === "changeTheme") {
        if (data.themeMode) tvSettings.setThemeMode(data.themeMode);
        if (data.primaryColor) tvSettings.setPrimaryColor(data.primaryColor);
        if (data.showTomorrow !== undefined && data.showTomorrow !== null) tvSettings.setShowTomorrow(data.showTomorrow);
        if (data.zoomLevel !== undefined && data.zoomLevel !== null) tvSettings.setZoomLevel(data.zoomLevel);
        if (data.adhanEnabled !== undefined && data.adhanEnabled !== null) tvSettings.setAdhanEnabled(data.adhanEnabled);
        if (data.iqamaEnabled !== undefined && data.iqamaEnabled !== null) tvSettings.setIqamaEnabled(data.iqamaEnabled);
        if (data.adhanCountdownMinutes !== undefined && data.adhanCountdownMinutes !== null) tvSettings.setAdhanCountdownMinutes(data.adhanCountdownMinutes);
        if (data.iqamaCountdownMinutes !== undefined && data.iqamaCountdownMinutes !== null) tvSettings.setIqamaCountdownMinutes(data.iqamaCountdownMinutes);
        if (data.adhanScreenDurationMinutes !== undefined && data.adhanScreenDurationMinutes !== null) tvSettings.setAdhanScreenDurationMinutes(data.adhanScreenDurationMinutes);
        if (data.iqamaScreenDurationMinutes !== undefined && data.iqamaScreenDurationMinutes !== null) tvSettings.setIqamaScreenDurationMinutes(data.iqamaScreenDurationMinutes);
        // Jummah silence settings
        if (data.jummahSilenceEnabled !== undefined && data.jummahSilenceEnabled !== null) tvSettings.setJummahSilenceEnabled(data.jummahSilenceEnabled);
        if (data.jummahSilenceDurationMinutes !== undefined && data.jummahSilenceDurationMinutes !== null) tvSettings.setJummahSilenceDurationMinutes(data.jummahSilenceDurationMinutes);
        // Forbidden screen settings
        if (data.forbiddenScreenEnabled !== undefined && data.forbiddenScreenEnabled !== null) tvSettings.setForbiddenScreenEnabled(data.forbiddenScreenEnabled);
        if (data.forbiddenScreenDurationMinutes !== undefined && data.forbiddenScreenDurationMinutes !== null) tvSettings.setForbiddenScreenDurationMinutes(data.forbiddenScreenDurationMinutes);
        // Tomorrow changes banner
        if (data.tomorrowChangesBanner !== undefined && data.tomorrowChangesBanner !== null) tvSettings.setTomorrowChangesBanner(data.tomorrowChangesBanner);
      }

      // Handle resetSettings command (reset all to defaults)
      if (data.command === "resetSettings") {
        tvSettings.resetToDefaults();
      }

      // Handle theme mode setting (extended - light/dark/system/auto)
      if (data.command === "setThemeMode" && data.themeMode) {
        tvSettings.setThemeMode(data.themeMode);
      }

      // Handle primary color setting
      if (data.command === "setPrimaryColor" && data.primaryColor) {
        tvSettings.setPrimaryColor(data.primaryColor);
      }

      // Handle custom color setting
      if (data.command === "setCustomColor" && data.primaryColor) {
        tvSettings.setCustomColor(data.primaryColor);
      }

      // Handle show tomorrow setting (not used in Today view but keep for consistency)
      if (data.command === "setShowTomorrow" && data.showTomorrow !== undefined) {
        tvSettings.setShowTomorrow(data.showTomorrow);
      }

      // Handle adhan/iqama enabled settings
      if (data.command === "setAdhanEnabled" && data.adhanEnabled !== undefined) {
        tvSettings.setAdhanEnabled(data.adhanEnabled);
      }
      if (data.command === "setIqamaEnabled" && data.iqamaEnabled !== undefined) {
        tvSettings.setIqamaEnabled(data.iqamaEnabled);
      }

      // Handle countdown minute settings
      if (data.command === "setAdhanCountdownMinutes" && data.adhanCountdownMinutes !== undefined) {
        tvSettings.setAdhanCountdownMinutes(data.adhanCountdownMinutes);
      }
      if (data.command === "setIqamaCountdownMinutes" && data.iqamaCountdownMinutes !== undefined) {
        tvSettings.setIqamaCountdownMinutes(data.iqamaCountdownMinutes);
      }
      if (data.command === "setIqamaScreenDurationMinutes" && data.iqamaScreenDurationMinutes !== undefined) {
        tvSettings.setIqamaScreenDurationMinutes(data.iqamaScreenDurationMinutes);
      }

      // Handle bulk settings update (update multiple settings at once)
      if (data.command === "updateSettings") {
        if (data.themeMode) tvSettings.setThemeMode(data.themeMode);
        if (data.primaryColor) tvSettings.setPrimaryColor(data.primaryColor);
        if (data.showTomorrow !== undefined && data.showTomorrow !== null) tvSettings.setShowTomorrow(data.showTomorrow);
        if (data.adhanEnabled !== undefined && data.adhanEnabled !== null) tvSettings.setAdhanEnabled(data.adhanEnabled);
        if (data.iqamaEnabled !== undefined && data.iqamaEnabled !== null) tvSettings.setIqamaEnabled(data.iqamaEnabled);
        if (data.adhanCountdownMinutes !== undefined && data.adhanCountdownMinutes !== null) tvSettings.setAdhanCountdownMinutes(data.adhanCountdownMinutes);
        if (data.iqamaCountdownMinutes !== undefined && data.iqamaCountdownMinutes !== null) tvSettings.setIqamaCountdownMinutes(data.iqamaCountdownMinutes);
        if (data.adhanScreenDurationMinutes !== undefined && data.adhanScreenDurationMinutes !== null) tvSettings.setAdhanScreenDurationMinutes(data.adhanScreenDurationMinutes);
        if (data.iqamaScreenDurationMinutes !== undefined && data.iqamaScreenDurationMinutes !== null) tvSettings.setIqamaScreenDurationMinutes(data.iqamaScreenDurationMinutes);
        // Jummah silence settings
        if (data.jummahSilenceEnabled !== undefined && data.jummahSilenceEnabled !== null) tvSettings.setJummahSilenceEnabled(data.jummahSilenceEnabled);
        if (data.jummahSilenceDurationMinutes !== undefined && data.jummahSilenceDurationMinutes !== null) tvSettings.setJummahSilenceDurationMinutes(data.jummahSilenceDurationMinutes);
        // Forbidden screen settings
        if (data.forbiddenScreenEnabled !== undefined && data.forbiddenScreenEnabled !== null) tvSettings.setForbiddenScreenEnabled(data.forbiddenScreenEnabled);
        if (data.forbiddenScreenDurationMinutes !== undefined && data.forbiddenScreenDurationMinutes !== null) tvSettings.setForbiddenScreenDurationMinutes(data.forbiddenScreenDurationMinutes);
        // Tomorrow changes banner
        if (data.tomorrowChangesBanner !== undefined && data.tomorrowChangesBanner !== null) tvSettings.setTomorrowChangesBanner(data.tomorrowChangesBanner);
      }

      // Handle refresh command
      if (data.command === "refresh") {
        // Set flag to enter fullscreen after reload
        sessionStorage.setItem("tv-enter-fullscreen-after-refresh", "true");
        window.location.reload();
      }

      // Handle dismiss overlay command
      if (data.command === "dismissOverlay") {
        // Clear test mode if active
        if (testTimerRef.current) {
          clearInterval(testTimerRef.current);
          testTimerRef.current = null;
        }
        setTestMode(null);
        clearOverlay();
      }

      // Handle prayer times updated command (when backend prayer times change)
      if (data.command === "prayerTimesUpdated") {
        console.log("[TV Prayer Times] Prayer times updated, reloading data...");
        loadPrayerTimes(masjidId);
      }
    });

    return () => {
      unsubscribeStatus();
      unsubscribeCommand();
      releaseTvSse();
    };
  }, [masjidId, user?.uid, navigate, masjidData?.subscription]);

  // Note: updateTvLastView is called in the status listener above when clientId is received.
  // No separate useEffect needed - the SSE connection handles view reporting.

  // Push settings changes to backend so app remote can sync
  // This runs when any setting changes on the TV side
  useEffect(() => {
    if (!masjidId || !user) return;
    
    // Debounce: skip during initial mount/hydration
    const timer = setTimeout(() => {
      const suppressNetwork = Date.now() - remoteSettingsSyncRef.current < REMOTE_SETTINGS_SUPPRESS_MS;
      updateTvSettings({
        themeMode: tvSettings.themeMode,
        primaryColor: tvSettings.primaryColor,
        showTomorrow: tvSettings.showTomorrow,
        zoomLevel: tvSettings.zoomLevel,
        adhanEnabled: tvSettings.adhanEnabled,
        iqamaEnabled: tvSettings.iqamaEnabled,
        adhanCountdownMinutes: tvSettings.adhanCountdownMinutes,
        iqamaCountdownMinutes: tvSettings.iqamaCountdownMinutes,
        adhanScreenDurationMinutes: tvSettings.adhanScreenDurationMinutes,
        iqamaScreenDurationMinutes: tvSettings.iqamaScreenDurationMinutes,
        jummahSilenceEnabled: tvSettings.jummahSilenceEnabled,
        jummahSilenceDurationMinutes: tvSettings.jummahSilenceDurationMinutes,
        forbiddenScreenEnabled: tvSettings.forbiddenScreenEnabled,
        forbiddenScreenDurationMinutes: tvSettings.forbiddenScreenDurationMinutes,
        tomorrowChangesBanner: tvSettings.tomorrowChangesBanner,
      }, { suppressNetwork });
    }, 100);
    
    return () => clearTimeout(timer);
  }, [
    masjidId,
    user?.uid,
    tvSettings.themeMode,
    tvSettings.primaryColor,
    tvSettings.showTomorrow,
    tvSettings.zoomLevel,
    tvSettings.adhanEnabled,
    tvSettings.iqamaEnabled,
    tvSettings.adhanCountdownMinutes,
    tvSettings.iqamaCountdownMinutes,
    tvSettings.adhanScreenDurationMinutes,
    tvSettings.iqamaScreenDurationMinutes,
    tvSettings.jummahSilenceEnabled,
    tvSettings.jummahSilenceDurationMinutes,
    tvSettings.forbiddenScreenEnabled,
    tvSettings.forbiddenScreenDurationMinutes,
    tvSettings.tomorrowChangesBanner,
  ]);

  const loadPrayerTimes = async (mId) => {
    try {
      // Load TV data from API (masjid + prayer times)
      const tvData = await getTvData(mId);
      if (tvData.ok) {
        setTodayPrayerTimes(tvData.todayPrayerTimes);
        setTomorrowPrayerTimes(tvData.tomorrowPrayerTimes);
      }
    } catch (err) {
      console.error("Error loading prayer times:", err);
    }
  };

  // Refresh prayer times every 5 minutes in the background
  useEffect(() => {
    if (!masjidId) return;
    const refreshInterval = setInterval(() => {
      loadPrayerTimes(masjidId);
    }, 5 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, [masjidId]);

  // Note: Countdown logic and formatTimeDisplay are now handled by useCountdownOverlay hook

  // Strips the PM/AM part from the time string for display
  const formatTimeNoSuffix = (timeStr) => {
    const formatted = formatTimeDisplay(timeStr);
    return formatted.replace(/\s*(AM|PM)/i, "");
  };

  // Gets just the suffix
  const getTimeSuffix = (timeStr, prayerKey) => {
    const formatted = formatTimeDisplay(timeStr, prayerKey);
    const match = formatted.match(/(AM|PM)/i);
    return match ? match[0] : "";
  };

  // Compute prayer times changing tomorrow (adhan and/or iqama)
  const tomorrowChanges = useMemo(() => {
    if (!todayPrayerTimes || !tomorrowPrayerTimes) return [];
    const prayers = [
      { key: "fajr", label: "Fajr" },
      { key: "duhr", label: "Dhuhr" },
      { key: "asr", label: "Asr" },
      { key: "maghrib", label: "Maghrib" },
      { key: "isha", label: "Isha" },
    ];
    const alwaysShow = new Set(["duhr", "asr"]);
    const changes = [];
    for (const p of prayers) {
      const todayAdhan = todayPrayerTimes[p.key]?.adhan;
      const tomorrowAdhan = tomorrowPrayerTimes[p.key]?.adhan;
      const todayIqama = todayPrayerTimes[p.key]?.iqama;
      const tomorrowIqama = tomorrowPrayerTimes[p.key]?.iqama;
      if (alwaysShow.has(p.key) || todayAdhan !== tomorrowAdhan || todayIqama !== tomorrowIqama) {
        changes.push({
          label: p.label,
          adhan: tomorrowAdhan,
          iqama: tomorrowIqama,
        });
      }
    }
    return changes;
  }, [todayPrayerTimes, tomorrowPrayerTimes]);

  const showTomorrowBanner = tvSettings.tomorrowChangesBanner && tomorrowChanges.length > 0;
  const jummahTimes = todayPrayerTimes?.jummah
    ? Object.entries(todayPrayerTimes.jummah).filter(([_, time]) => time && time.trim())
    : [];
  const khutbahTimes = todayPrayerTimes?.khutbah
    ? Object.entries(todayPrayerTimes.khutbah).filter(([_, time]) => time && time.trim())
    : [];
  const hasJummah = jummahTimes.length > 0;
  const hasKhutbah = khutbahTimes.length > 0;
  const hasSunrise = Boolean(todayPrayerTimes?.sunrise);
  const jummahCount = jummahTimes.length;
  const showSupplementaryRow = hasSunrise || hasJummah;
  const useCompactPrayerTable = showTomorrowBanner;
  const supplementaryRowHeight = !showSupplementaryRow
    ? "0px"
    : hasJummah && (hasKhutbah || jummahCount > 1)
      ? (showTomorrowBanner ? "13vh" : "14.5vh")
      : hasJummah
        ? (showTomorrowBanner ? "11.5vh" : "12.5vh")
        : (showTomorrowBanner ? "8.5vh" : "9.5vh");
  const headerHeight = showTomorrowBanner ? "8.5vh" : "10vh";
  const bannerHeight = showTomorrowBanner ? "5vh" : "0vh";
  const mainContentHeight = showTomorrowBanner
    ? "calc(100vh - 8.5vh - 5vh)"
    : "calc(100vh - 10vh)";
  const mainContentHorizontalPadding = showTomorrowBanner ? "1.1vw" : "1.5vw";
  const mainContentTopPadding = showTomorrowBanner ? "1.1vw" : "1.5vw";
  const mainContentBottomPadding = showTomorrowBanner ? "2.8vh" : "1.5vw";
  const controlsBottomOffset = "2vh";

  const isDark = localAppearance === "dark";
  const bgColor = isDark ? "bg-[#0a0a0a]" : "bg-[#faf7f2]";
  const textColor = isDark ? "text-white" : "text-[#0f172a]";
  // Match TeleView PrayerCard colors exactly
  const mainTextColor = isDark ? "#ffffff" : "#0f172a";
  const mutedTextColor = isDark ? "rgba(255,255,255,0.6)" : "#64748b";
  const cardBg = isDark ? "bg-[#171717]" : "bg-[#fffdf9]";
  const cardBorder = isDark ? "" : "border border-[#e8e4dc]";
  const headerBorder = isDark ? "border-white/10" : "border-[#e8e4dc]";
  const rowHover = isDark ? "hover:bg-white/5" : "hover:bg-[#faf7f2]";
  const rowBorderColor = isDark ? "border-white/5" : "border-[#f0ebe0]";
  // Use tvSettings primary color for next prayer accent
  const primaryColor = tvSettings.primaryColor || theme?.colors?.primary || "#bf9a2f";
  const rowHighlightBg = isDark ? `${primaryColor}1a` : `${primaryColor}1a`;
  const nextPrayerTextColor = primaryColor;
  const nextPrayerTextSoft = isDark ? `${primaryColor}cc` : `${primaryColor}cc`;
  const tableHeaderPaddingY = useCompactPrayerTable ? "0.35vh" : "0.6vh";
  const tableHeaderFontSize = useCompactPrayerTable ? "1.8vh" : "2.2vh";
  const prayerLabelFontSize = useCompactPrayerTable ? "4.4vh" : "5.5vh";
  const adhanTimeFontSize = useCompactPrayerTable ? "8vh" : "10vh";
  const iqamaTimeFontSize = useCompactPrayerTable ? "8vh" : "10vh";
  const adhanDoubleDigitFontSize = useCompactPrayerTable ? "7.2vh" : adhanTimeFontSize;
  const iqamaDoubleDigitFontSize = useCompactPrayerTable ? "7.2vh" : iqamaTimeFontSize;
  const adhanSuffixFontSize = useCompactPrayerTable ? "4.2vh" : "5.5vh";
  const iqamaSuffixFontSize = useCompactPrayerTable ? "4.2vh" : "5.5vh";
  const adhanDoubleDigitSuffixFontSize = useCompactPrayerTable ? "3.8vh" : adhanSuffixFontSize;
  const iqamaDoubleDigitSuffixFontSize = useCompactPrayerTable ? "3.8vh" : iqamaSuffixFontSize;
  const prayerRowLineHeight = useCompactPrayerTable ? 0.84 : 0.88;
  const prayerSuffixLineHeight = useCompactPrayerTable ? 0.8 : 0.84;


  if (loading) return (
    <div className={`h-screen w-screen flex items-center justify-center ${bgColor}`}>
      <Loader2 className="h-10 w-10 animate-spin text-primary" />
    </div>
  );

  return (
    <div
      className={`relative flex h-screen w-screen flex-col overflow-hidden ${bgColor} ${textColor} font-sans selection:bg-primary/20`}
      style={{ cursor: controlsVisible ? "auto" : "none" }}
    >
      {/* View Switching Loading Overlay */}
      {viewSwitching && (
        <div
          className="absolute inset-0 z-[99999] flex items-center justify-center"
          style={{
            // Opaque bg, no backdrop-filter (black-box artifact on the Android TV box WebView).
            backgroundColor: isDark ? "rgb(10,10,10)" : "rgb(250,247,242)",
          }}
        >
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="animate-spin" style={{ width: "8vh", height: "8vh", color: primaryColor }} />
            <p className="font-medium" style={{ fontSize: "2.5vh", color: isDark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)" }}>
              Switching View...
            </p>
          </div>
        </div>
      )}

      {/* Adhan/Iqama/Jummah/Forbidden Countdown & Announcement Overlay */}
      {!settingsOpen && <CountdownOverlay
        overlayMode={overlayMode}
        overlayPrayerName={overlayPrayerName}
        overlayCountdownSeconds={overlayCountdownSeconds}
        overlayType={overlayType}
        screenDimmed={screenDimmed}
        iqamaExtraDimmed={iqamaExtraDimmed}
        masjidData={masjidData}
        primaryColor={primaryColor}
        isDark={isDark}
        simplifiedPrayerTimes={todayPrayerTimes ? {
          fajr: { adhan: todayPrayerTimes?.fajr?.adhan || "-", iqama: todayPrayerTimes?.fajr?.iqama || "-" },
          sunrise: { adhan: todayPrayerTimes?.sunrise || "-", iqama: null },
          dhuhr: { adhan: todayPrayerTimes?.duhr?.adhan || "-", iqama: todayPrayerTimes?.duhr?.iqama || "-" },
          asr: { adhan: todayPrayerTimes?.asr?.adhan || "-", iqama: todayPrayerTimes?.asr?.iqama || "-" },
          maghrib: { adhan: todayPrayerTimes?.maghrib?.adhan || "-", iqama: todayPrayerTimes?.maghrib?.iqama || "-" },
          isha: { adhan: todayPrayerTimes?.isha?.adhan || "-", iqama: todayPrayerTimes?.isha?.iqama || "-" },
        } : null}
      />}

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
           style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }}
      />

      {/* Header */}
      {/* NOTE: no backdrop-blur here. backdrop-filter renders as an opaque black box
          on the Android TV box's WebView GPU, and the header bg is already a near-solid color. */}
      <div className={`px-[2vw] flex items-center justify-between border-b ${headerBorder} relative z-10`}
          style={{
           height: headerHeight,
           backgroundColor: isDark ? 'rgba(10, 10, 10, 0.5)' : 'rgba(250, 247, 242, 0.5)',
          }}
      >
         <div>
            {/* Increased Title Size */}
          <h1 className="font-bold tracking-tight leading-tight" style={{ color: mainTextColor, fontSize: showTomorrowBanner ? '3.5vh' : '4vh' }}>{masjidData?.name || "Masjid"}</h1>
          <div className="flex items-center gap-[1vh] mt-[0.2vh]" style={{ color: mutedTextColor, fontSize: showTomorrowBanner ? '2vh' : '2.2vh' }}>
            <MapPin className="h-[2.2vh] w-[2.2vh]" />
               <span>{masjidData?.city || "City"}, {masjidData?.state || "State"}</span>
            </div>
         </div>
         <div className="flex items-center gap-[2vh] pr-[1vw]">
            <div className="flex items-center justify-center">
               <span
              className="font-bold"
              style={{ color: primaryColor, fontSize: showTomorrowBanner ? '5vh' : '5.5vh' }}
               >
                  Muslim+
               </span>
            </div>
         </div>
      </div>

      {/* Main Content - Reduced padding to 1.5vw to maximize width */}
        <div className="min-h-0 grid grid-cols-12 gap-[2vw]"
          style={{
           height: mainContentHeight,
           paddingTop: mainContentTopPadding,
           paddingRight: mainContentHorizontalPadding,
           paddingBottom: mainContentBottomPadding,
           paddingLeft: mainContentHorizontalPadding,
          }}
      >

        {/* Left: Countdown & Next Prayer */}
        <div className="col-span-5 flex flex-col h-full">
          <Card className={`flex-1 shadow-2xl ${cardBg} ${cardBorder} relative overflow-hidden flex flex-col items-center p-[2vh]`}>
            {/* Gradient glow. The radial gradient already fades softly to transparent, so we
                do NOT apply a CSS blur filter here: filter: blur() forces an offscreen GPU
                buffer that the Android TV box's WebView renders as an opaque black box. */}
            <div
              className="absolute -inset-[20%]"
              style={{
                background: `radial-gradient(ellipse at 30% 30%, ${primaryColor}18 0%, transparent 60%)`,
              }}
            />

            <div className="relative z-10 text-center flex flex-col h-full w-full">

              {/* Date Section */}
              <div className="flex-[1] flex flex-col justify-center">
                 {todayPrayerTimes?.islamicDate && (
                    <div className="font-sans tracking-wide uppercase mb-[1vh]" style={{ color: mutedTextColor, fontSize: '6vh' }}>
                       {todayPrayerTimes.islamicDate}
                    </div>
                 )}
            {/* Date display */}
            <div className="font-sans tracking-widest font-bold uppercase border-b-2 pb-[1vh] inline-block px-[2vw]" style={{ color: mainTextColor, borderColor: `${primaryColor}4d`, fontSize: '4.5vh' }}>
              {currentDate.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}
            </div>
          </div>

          {/* Clock Section - MAXIMIZED */}
          <div className="flex-[2.5] flex items-center justify-center overflow-hidden">
            {/* Clock sized to fit container width */}
            <div className="leading-none font-sans font-normal tracking-tighter flex items-baseline" style={{ color: mainTextColor, fontSize: '18vh', transform: 'scale(1.0, 1.15)' }}>
              {currentDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }).replace(/\s*(AM|PM)/i, "")}
              <span className="ml-[0.5vw] font-sans font-normal" style={{ fontSize: '18vh' }}>
                {currentDate.toLocaleTimeString([], { hour12: true }).slice(-2)}
              </span>
            </div>
          </div>

          {/* Countdown Section */}
          <div className={`flex-[1] border-t-2 flex flex-col items-center justify-center`} style={{ borderColor: `${primaryColor}4d` }}>
           {/* Post-Isha: Show Fajr time prominently, countdown below */}
           {isTomorrowFajr && tomorrowFajrTimeStr ? (
             <div className="flex flex-col items-center justify-center">
               <span className="uppercase tracking-widest font-normal mb-[-1vh]" style={{ color: mutedTextColor, fontSize: '3vh' }}>
                 Tomorrow Fajr
               </span>
               <span className="font-sans font-bold leading-none" style={{ color: primaryColor, fontSize: '13vh' }}>
                 {formatTimeDisplay(tomorrowFajrTimeStr, "fajr")}
               </span>
               <div className="flex items-center justify-center gap-[0.8vw] mt-[0.5vh]">
                 <span className="uppercase tracking-widest font-normal" style={{ color: mutedTextColor, fontSize: '3vh' }}>
                   Adhan in
                 </span>
                 <span className="font-sans tabular-nums font-bold" style={{ color: mainTextColor, fontSize: '3vh' }}>
                   {countdown || "--:--:--"}
                 </span>
               </div>
             </div>
           ) : (
             <div className="flex flex-col items-center justify-center">
               <span className="uppercase tracking-widest font-normal mb-[-1vh]" style={{ color: mutedTextColor, fontSize: '3vh' }}>
                {countdownLabel}
               </span>
               <span className="font-sans tabular-nums font-bold leading-none" style={{ color: mainTextColor, fontSize: '13vh' }}>
                 {countdown || "--:--:--"}
               </span>
             </div>
           )}
          </div>
        </div>
       </Card>
      </div>

        {/* Right: Prayer List */}
        <div className="col-span-7 grid h-full min-h-0 gap-[1vh]"
             style={{
               gridTemplateRows: showSupplementaryRow
                 ? (showTomorrowBanner
                   ? `minmax(0, 1fr) ${supplementaryRowHeight}`
                   : 'minmax(0, 1fr) auto')
                 : 'minmax(0, 1fr)',
             }}>
          {/* No backdrop-blur: the card bg is opaque so blur is invisible, but backdrop-filter
              triggers the Android TV box black-box artifact. */}
          <Card className={`min-h-0 shadow-xl ${cardBg} ${cardBorder} overflow-hidden flex flex-col`}>
            <CardContent className="min-h-0 p-0 flex-1 flex flex-col">
              {/* Header Row */}
              <div className={`grid px-[2vw] border-b ${isDark ? 'border-white/20' : 'border-[#e8e4dc]'}`}
                   style={{
                     backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#faf7f2',
                     gridTemplateColumns: '1fr 1fr 1fr',
                     paddingTop: tableHeaderPaddingY,
                     paddingBottom: tableHeaderPaddingY,
                   }}
              >
                <div className="font-semibold uppercase tracking-wider" style={{ color: mutedTextColor, fontSize: tableHeaderFontSize }}>Prayer</div>
                <div className="font-semibold text-center uppercase tracking-wider" style={{ color: mutedTextColor, fontSize: tableHeaderFontSize }}>Adhan</div>
                <div className="font-semibold text-center uppercase tracking-wider" style={{ color: primaryColor, fontSize: tableHeaderFontSize }}>Iqamah</div>
              </div>

              <div className="min-h-0 flex-1 flex flex-col justify-evenly overflow-hidden">
              {[
                { key: "fajr", label: "Fajr" },
                { key: "duhr", label: "Dhuhr" },
                { key: "asr", label: "Asr" },
                { key: "maghrib", label: "Maghrib" },
                { key: "isha", label: "Isha" },
              ].map((p) => {
                const isNext = activePrayer === p.key;

                // Check if hour is double digit (10, 11, 12) for font size adjustment
                const adhanTime = formatTimeNoSuffix(todayPrayerTimes?.[p.key]?.adhan);
                const iqamaTime = formatTimeNoSuffix(todayPrayerTimes?.[p.key]?.iqama);
                const isAdhanDoubleDigit = adhanTime && /^1[0-2]:/.test(adhanTime);
                const isIqamaDoubleDigit = iqamaTime && /^1[0-2]:/.test(iqamaTime);

                return (
                  <div
                    key={p.key}
                    className={`relative grid px-[2vw] items-center transition-colors flex-1
                                ${isNext ? '' : `${rowHover} border-b ${rowBorderColor} last:border-b-0`}
                      `}
                    style={isNext ? { backgroundColor: rowHighlightBg, gridTemplateColumns: '0.8fr 1fr 1fr' } : { gridTemplateColumns: '0.8fr 1fr 1fr' }}
                  >
                    {isNext && (
                      <span
                        aria-hidden="true"
                        className="absolute start-0 top-1/2 -translate-y-1/2 rounded-sm"
                        style={{ width: '0.6vh', height: '6vh', backgroundColor: primaryColor }}
                      />
                    )}
                    {/* Prayer Name - matching Islamic date font style */}
                    <div
                      className="font-sans tracking-wide uppercase ps-[1.5vh]"
                      style={{
                        color: isNext ? nextPrayerTextColor : mainTextColor,
                        fontSize: prayerLabelFontSize,
                        fontWeight: 500,
                        lineHeight: prayerRowLineHeight,
                      }}
                    >
                      {p.label}
                    </div>
                    {/* Time Columns - responsive sizing, slightly smaller for double-digit hours */}
                    <div className="font-sans font-bold leading-none flex items-baseline justify-end pr-[1vw]" style={{ color: mainTextColor, fontSize: isAdhanDoubleDigit ? adhanDoubleDigitFontSize : adhanTimeFontSize, lineHeight: prayerRowLineHeight }}>
                      <span className="tabular-nums leading-none">{adhanTime}</span>
                      <span className="font-medium leading-none" style={{ color: mutedTextColor, fontSize: isAdhanDoubleDigit ? adhanDoubleDigitSuffixFontSize : adhanSuffixFontSize, lineHeight: prayerSuffixLineHeight }}>
                        {getTimeSuffix(todayPrayerTimes?.[p.key]?.adhan, p.key)}
                      </span>
                    </div>
                    <div className="font-sans font-extrabold leading-none flex items-baseline justify-end pr-[1vw]" style={{ color: primaryColor, fontSize: isIqamaDoubleDigit ? iqamaDoubleDigitFontSize : iqamaTimeFontSize, lineHeight: prayerRowLineHeight }}>
                      <span className="tabular-nums leading-none">{iqamaTime}</span>
                      <span className="font-medium leading-none" style={{ color: `${primaryColor}cc`, fontSize: isIqamaDoubleDigit ? iqamaDoubleDigitSuffixFontSize : iqamaSuffixFontSize, lineHeight: prayerSuffixLineHeight }}>
                        {getTimeSuffix(todayPrayerTimes?.[p.key]?.iqama, p.key)}
                      </span>
                    </div>
                  </div>
                );
              })}
              </div>
            </CardContent>
          </Card>

          {/* Sunrise & Jummah Section */}
          {showSupplementaryRow && (
              <div
                className={`${showTomorrowBanner ? "h-full " : ""}w-full shrink-0 gap-[1vh]`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: hasSunrise && hasJummah
                    ? (jummahCount >= 2 ? 'auto auto 1fr' : '1fr 1.5fr')
                    : hasSunrise && !hasJummah
                      ? '1fr 1fr'
                      : '1fr',
                }}
              >
                {/* QR Code Card - separate card when 2 Jummah times */}
                {hasSunrise && jummahCount >= 2 && (
                  <Card className={`${cardBg} ${cardBorder} shadow-lg flex items-center justify-center h-full overflow-hidden`}>
                    <CardContent className="flex items-center justify-center h-full p-[0.5vh] overflow-hidden">
                      <div style={{ background: "white", borderRadius: "1.2vh", padding: "0.5vh", height: "100%", aspectRatio: "1/1", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <QRCodeWrapper
                          value="https://muslimplus.org"
                          size={Math.round(window.innerHeight * (hasKhutbah ? 0.10 : 0.08))}
                        />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Sunrise Card */}
                {hasSunrise && (
                  <Card className={`${cardBg} ${cardBorder} shadow-lg flex items-center justify-center h-full overflow-hidden`}>
                    <CardContent className="flex items-center justify-center gap-[1.5vw] w-full p-[0.8vh] px-[1vw] overflow-hidden">
                      <div className={`flex ${jummahCount >= 2 ? 'flex-col items-center' : 'items-center gap-[1.5vw]'}`}>
                        <span
                          className="font-semibold flex items-center shrink-0"
                          style={{
                            fontSize: '2.8vh',
                            color: mutedTextColor,
                          }}
                        >
                          <SunriseIcon
                            className="mr-[0.5vh] shrink-0"
                            style={{
                              width: '2.8vh',
                              height: '2.8vh',
                            }}
                          />
                          Sunrise
                        </span>
                        <div className="flex items-baseline gap-[0.2vw]">
                          <span
                            className="font-sans font-bold"
                            style={{
                              fontSize: '3.5vh',
                              color: mainTextColor,
                            }}
                          >
                            {formatTimeNoSuffix(todayPrayerTimes.sunrise)}
                          </span>
                          <span
                            className="font-medium"
                            style={{
                              fontSize: '2.2vh',
                              color: mutedTextColor,
                            }}
                          >
                            {getTimeSuffix(todayPrayerTimes.sunrise, 'sunrise')}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Jummah Card */}
                {hasJummah && (
                  <Card className={`${cardBg} ${cardBorder} shadow-lg flex items-center h-full overflow-hidden`}>
                    <CardContent className="flex items-center w-full h-full p-[0.8vh] px-[1.5vw] overflow-hidden">
                      <div className={`flex items-center w-full ${jummahCount === 1 ? 'justify-between' : 'justify-center gap-[4vw]'}`}>
                        <div className={`flex items-center ${jummahCount >= 2 ? '' : 'flex-1 justify-center'}`}>
                          <div className="flex items-center gap-[4vw]">
                        {jummahTimes.map(([key, time], index) => {
                          const khutbahTime = khutbahTimes[index]?.[1];
                          const isFirst = index === 0;
                          const timeSize = '3.8vh';
                          const suffixSize = '2.2vh';
                          const labelSize = '1.8vh';

                          return (
                            <div
                              key={key}
                              className="flex flex-col gap-[-0.2vh]"
                            >
                              {/* Jummah Header */}
                              <span
                                className="font-bold uppercase tracking-wider mb-[0.2vh]"
                                style={{ fontSize: '2.5vh', color: mutedTextColor }}
                              >
                                {jummahCount === 1 ? 'JUMMAH' : (isFirst ? 'JUMMAH I' : 'JUMMAH II')}
                              </span>
                              {/* Khutbah row */}
                              {khutbahTime && (
                                <div className="flex items-baseline gap-[0.8vw]">
                                  <span className="flex items-baseline">
                                    <span
                                      className="font-sans font-bold leading-none"
                                      style={{ fontSize: timeSize, color: mainTextColor, fontVariantNumeric: 'tabular-nums' }}
                                    >
                                      {formatTimeNoSuffix(khutbahTime)}
                                    </span>
                                    <span
                                      className="font-medium"
                                      style={{ fontSize: suffixSize, color: mutedTextColor }}
                                    >
                                      {getTimeSuffix(khutbahTime, 'khutbah')}
                                    </span>
                                  </span>
                                  <span
                                    className="font-bold uppercase tracking-wider"
                                    style={{ fontSize: labelSize, color: mutedTextColor }}
                                  >
                                    Khutbah
                                  </span>
                                </div>
                              )}
                              {/* Prayer row */}
                              <div className="flex items-baseline gap-[0.8vw]">
                                <span className="flex items-baseline">
                                  <span
                                    className="font-sans font-bold leading-none"
                                    style={{ fontSize: timeSize, color: primaryColor, fontVariantNumeric: 'tabular-nums' }}
                                  >
                                    {formatTimeNoSuffix(time)}
                                  </span>
                                  <span
                                    className="font-medium"
                                    style={{ fontSize: suffixSize, color: `${primaryColor}cc` }}
                                  >
                                    {getTimeSuffix(time, 'jummah')}
                                  </span>
                                </span>
                                <span
                                  className="font-bold uppercase tracking-wider"
                                  style={{ fontSize: labelSize, color: mutedTextColor }}
                                >
                                  Prayer
                                </span>
                              </div>
                            </div>
                          );
                        })}
                          </div>
                        </div>
                        {/* QR Code when only 1 Jummah - appears on the right */}
                        {jummahCount === 1 && (
                          <div className="flex items-center justify-center shrink-0 pr-[1vw]">
                            <div style={{ background: "white", borderRadius: "1.2vh", padding: "0.8vh" }}>
                              <QRCodeWrapper
                                value="https://muslimplus.org"
                                size={Math.round(window.innerHeight * 0.08)}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* QR Code Card - when sunrise exists but no Jummah */}
                {hasSunrise && !hasJummah && (
                  <Card className={`${cardBg} ${cardBorder} shadow-lg flex items-center justify-center h-full overflow-hidden`}>
                    <CardContent className="flex items-center justify-center w-full p-[0.8vh] px-[1.5vw] overflow-hidden">
                      <div style={{ background: "white", borderRadius: "1.2vh", padding: "0.8vh" }}>
                        <QRCodeWrapper
                          value="https://muslimplus.org"
                          size={Math.round(window.innerHeight * 0.08)}
                        />
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
          )}
        </div>
      </div>

      {/* Tomorrow Changes Sliding Banner */}
      {showTomorrowBanner && (
        <div
          className="relative z-20 shrink-0 overflow-hidden"
          style={{
            height: bannerHeight,
            backgroundColor: isDark ? 'rgba(23, 23, 23, 0.95)' : 'rgba(255, 253, 249, 0.95)',
            borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e8e4dc'}`,
          }}
        >
          <div className="flex h-full items-center justify-center gap-[1.8vw] px-[1.5vw] whitespace-nowrap">
            <span
              className="font-semibold uppercase tracking-wider"
              style={{ fontSize: '1.8vh', color: '#ffffff' }}
            >
              Tomorrow:
            </span>
            {tomorrowChanges.map((change) => (
              <span key={change.label} className="flex items-center gap-[0.45vw]">
                <span className="font-sans tracking-wide uppercase" style={{ fontSize: '1.85vh', color: mainTextColor, fontWeight: 500 }}>
                  {change.label}
                </span>
                {change.adhan && (
                  <span style={{ fontSize: '1.85vh', color: mutedTextColor }}>
                    {formatTimeDisplay(change.adhan)}
                  </span>
                )}
                {change.adhan && change.iqama && (
                  <span style={{ fontSize: '1.6vh', color: mutedTextColor }}>/</span>
                )}
                {change.iqama && (
                  <span className="font-semibold" style={{ fontSize: '1.85vh', color: primaryColor }}>
                    {formatTimeDisplay(change.iqama)}
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Controls Overlay - Simplified: Back, Fullscreen, Settings */}
      <TVControls
        visible={controlsVisible}
        isDark={isDark}
        isFullscreen={isFullscreen}
        bottomOffset={controlsBottomOffset}
        onBack={() => {
          sessionStorage.setItem('exit-fullscreen', 'true');
          sessionStorage.removeItem('tv-fullscreen-state');
          navigate(isSuperAdmin ? "/admin-dashboard" : "/masjid-dashboard");
        }}
        onFullscreenToggle={handleFullscreenToggle}
        onSettingsClick={() => setSettingsOpen(true)}
        extraControls={
          TEST_MODE && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (testMode === "adhan") {
                    if (testTimerRef.current) clearInterval(testTimerRef.current);
                    setTestMode(null);
                    clearOverlay();
                  } else {
                    if (testTimerRef.current) clearInterval(testTimerRef.current);
                    setTestMode("adhan");
                    setOverlayMode("countdown");
                    setOverlayPrayerName("Fajr");
                    setOverlayType("adhan");
                    setOverlayCountdownSeconds(10);

                    let seconds = 10;
                    let adhanScreenSeconds = 0;
                    testTimerRef.current = setInterval(() => {
                      seconds--;
                      if (seconds > 0) {
                        setOverlayCountdownSeconds(seconds);
                      } else if (seconds === 0) {
                        seconds = -1;
                        setOverlayMode("adhan");
                      } else {
                        adhanScreenSeconds++;
                        // Auto-stop after 10 seconds of adhan screen
                        if (adhanScreenSeconds >= 10) {
                          clearInterval(testTimerRef.current);
                          testTimerRef.current = null;
                          setTestMode(null);
                          clearOverlay();
                        }
                      }
                    }, 1000);
                  }
                }}
                style={{
                  width: "4vh",
                  height: "4vh",
                  borderRadius: "1.2vh",
                  background: testMode === "adhan" ? "rgba(34, 197, 94, 0.5)" : "rgba(34, 197, 94, 0.2)",
                  border: `2px solid ${testMode === "adhan" ? "rgba(34, 197, 94, 0.9)" : "rgba(34, 197, 94, 0.5)"}`,
                  // No backdrop-filter: paints as opaque black on the Android TV box's WebView GPU.
                  boxShadow: testMode === "adhan" ? "0 0 12px rgba(34, 197, 94, 0.5)" : "none",
                }}
                className="hover:scale-110 transition-transform duration-200"
                title={testMode === "adhan" ? "Stop Adhan Test" : "Test Adhan Sequence"}
              >
                <span style={{ fontSize: "1.5vh", color: "#22c55e", fontWeight: "bold" }}>A</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (testMode === "iqama") {
                    if (testTimerRef.current) clearInterval(testTimerRef.current);
                    setTestMode(null);
                    clearOverlay();
                  } else {
                    if (testTimerRef.current) clearInterval(testTimerRef.current);
                    setTestMode("iqama");
                    setOverlayMode("countdown");
                    setOverlayPrayerName("Fajr");
                    setOverlayType("iqama");
                    setOverlayCountdownSeconds(10);
                    setIqamaExtraDimmed(false);

                    let seconds = 10;
                    let iqamaSeconds = 0;
                    let extraDimApplied = false;
                    testTimerRef.current = setInterval(() => {
                      if (seconds > 0) {
                        seconds--;
                        setOverlayCountdownSeconds(seconds);
                      } else if (seconds === 0) {
                        seconds = -1;
                        setOverlayMode("iqama");
                        setScreenDimmed(true);
                      } else {
                        iqamaSeconds++;
                        // Auto-stop after 10 seconds of iqama screen
                        if (iqamaSeconds >= 10) {
                          clearInterval(testTimerRef.current);
                          testTimerRef.current = null;
                          setTestMode(null);
                          clearOverlay();
                        } else if (iqamaSeconds >= 5 && !extraDimApplied) {
                          extraDimApplied = true;
                          setIqamaExtraDimmed(true);
                        }
                      }
                    }, 1000);
                  }
                }}
                style={{
                  width: "4vh",
                  height: "4vh",
                  borderRadius: "1.2vh",
                  background: testMode === "iqama" ? "rgba(239, 68, 68, 0.5)" : "rgba(239, 68, 68, 0.2)",
                  border: `2px solid ${testMode === "iqama" ? "rgba(239, 68, 68, 0.9)" : "rgba(239, 68, 68, 0.5)"}`,
                  // No backdrop-filter: paints as opaque black on the Android TV box's WebView GPU.
                  boxShadow: testMode === "iqama" ? "0 0 12px rgba(239, 68, 68, 0.5)" : "none",
                }}
                className="hover:scale-110 transition-transform duration-200"
                title={testMode === "iqama" ? "Stop Iqama Test" : "Test Iqama Sequence"}
              >
                <span style={{ fontSize: "1.5vh", color: "#ef4444", fontWeight: "bold" }}>I</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (testMode === "jummah") {
                    if (testTimerRef.current) clearInterval(testTimerRef.current);
                    setTestMode(null);
                    clearOverlay();
                  } else {
                    if (testTimerRef.current) clearInterval(testTimerRef.current);
                    setTestMode("jummah");
                    setOverlayMode("jummah");
                    setOverlayPrayerName("Jummah");
                    setOverlayType("jummah");
                    setScreenDimmed(true);
                    setIqamaExtraDimmed(false);

                    let jummahSeconds = 0;
                    let extraDimApplied = false;
                    testTimerRef.current = setInterval(() => {
                      jummahSeconds++;
                      // Auto-stop after 10 seconds
                      if (jummahSeconds >= 10) {
                        clearInterval(testTimerRef.current);
                        testTimerRef.current = null;
                        setTestMode(null);
                        clearOverlay();
                      } else if (jummahSeconds >= 5 && !extraDimApplied) {
                        extraDimApplied = true;
                        setIqamaExtraDimmed(true);
                      }
                    }, 1000);
                  }
                }}
                style={{
                  width: "4vh",
                  height: "4vh",
                  borderRadius: "1.2vh",
                  background: testMode === "jummah" ? "rgba(168, 85, 247, 0.5)" : "rgba(168, 85, 247, 0.2)",
                  border: `2px solid ${testMode === "jummah" ? "rgba(168, 85, 247, 0.9)" : "rgba(168, 85, 247, 0.5)"}`,
                  // No backdrop-filter: paints as opaque black on the Android TV box's WebView GPU.
                  boxShadow: testMode === "jummah" ? "0 0 12px rgba(168, 85, 247, 0.5)" : "none",
                }}
                className="hover:scale-110 transition-transform duration-200"
                title={testMode === "jummah" ? "Stop Jummah Test" : "Test Jummah Silence"}
              >
                <span style={{ fontSize: "1.5vh", color: "#a855f7", fontWeight: "bold" }}>J</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (testMode === "sunrise") {
                    if (testTimerRef.current) clearInterval(testTimerRef.current);
                    setTestMode(null);
                    clearOverlay();
                  } else {
                    if (testTimerRef.current) clearInterval(testTimerRef.current);
                    setTestMode("sunrise");
                    setOverlayMode("forbidden");
                    setOverlayPrayerName("Sunrise");
                    setOverlayType("forbidden");
                    setOverlayCountdownSeconds(10);
                    setScreenDimmed(false);

                    let seconds = 10;
                    testTimerRef.current = setInterval(() => {
                      seconds--;
                      if (seconds > 0) {
                        setOverlayCountdownSeconds(seconds);
                      } else {
                        clearInterval(testTimerRef.current);
                        testTimerRef.current = null;
                        setTestMode(null);
                        clearOverlay();
                      }
                    }, 1000);
                  }
                }}
                style={{
                  width: "4vh",
                  height: "4vh",
                  borderRadius: "1.2vh",
                  background: testMode === "sunrise" ? "rgba(251, 191, 36, 0.5)" : "rgba(251, 191, 36, 0.2)",
                  border: `2px solid ${testMode === "sunrise" ? "rgba(251, 191, 36, 0.9)" : "rgba(251, 191, 36, 0.5)"}`,
                  // No backdrop-filter: paints as opaque black on the Android TV box's WebView GPU.
                  boxShadow: testMode === "sunrise" ? "0 0 12px rgba(251, 191, 36, 0.5)" : "none",
                }}
                className="hover:scale-110 transition-transform duration-200"
                title={testMode === "sunrise" ? "Stop Sunrise Test" : "Test Forbidden Prayer Time"}
              >
                <span style={{ fontSize: "1.5vh", color: "#fbbf24", fontWeight: "bold" }}>S</span>
              </Button>
            </>
          )
        }
      />

      {/* Settings Dialog */}
      <TVSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={tvSettings}
        isTeleView={false}
        onViewChange={(newView) => {
          if (newView === VIEW_MODES.TODAY) {
            // Already on Today view, do nothing
          } else if (newView === VIEW_MODES.MULTIDAY) {
            navigate("/admin/tv-viewer", { state: { viewMode: "none" } });
          } else if (newView === VIEW_MODES.EVENTS) {
            navigate("/admin/tv-viewer", { state: { viewMode: "events" } });
          } else if (newView === VIEW_MODES.IMAGES) {
            navigate("/admin/tv-viewer", { state: { viewMode: "images" } });
          }
        }}
      />

      {/* Wake lock to prevent screen sleep */}
      <WakeLockVideo />
    </div>
  );
}
